import { FC, useState } from 'react';

import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';

import { DeliveredEntriesSection } from '../components/DeliveredEntriesSection';
import { PendingAndHistoryTab } from '../components/PendingAndHistoryTab';
import { useGiftBrandSettlementData } from '../hooks/useGiftBrandSettlementData';
import { SETTLEMENT_TABS, SettlementTab } from '../utils/settlement.utils';

/**
 * 공급사/관리자 정산 관리 페이지.
 *
 * 데이터 페치는 `useGiftBrandSettlementData` 훅에 위임하고,
 * 이 컴포넌트는 탭 전환과 각 탭 컴포넌트 마운트만 담당한다.
 */
export const GiftBrandSettlementPage: FC = function GiftBrandSettlementPage() {
  const { giftBrandRole } = useAuth();
  const role = giftBrandRole() as LowerRole.Admin | LowerRole.GiftBrandSeller;
  const isAdminView = role === LowerRole.Admin;

  const [tab, setTab] = useState<SettlementTab>('pendingAndHistory');

  const {
    deliveredLines,
    deliveredLoading,
    pendingLines,
    pendingLoading,
    historyItems,
    historyLoading,
    mySettlementInfo,
    isMultiBrand,
    settlementByBrand,
    nextSettlement,
  } = useGiftBrandSettlementData(role);

  // admin 또는 여러 브랜드를 담당하는 셀러는 브랜드별(업체 컬럼·admin 스타일 표) 뷰로 표시한다.
  const brandView = isAdminView || isMultiBrand;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-bold">정산 관리</h2>
        <p className="mt-1 text-sm text-gray-500">
          배송완료된 건의 정산 금액을 확인하세요. 도매가에 소량 추가단가·택배비가 더해진
          금액입니다.
        </p>
      </div>

      {/* 탭 */}
      <div className="mb-4 border-b border-gray-200">
        <div className="flex gap-0">
          {SETTLEMENT_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`w-44 border-b-2 px-5 py-2.5 text-center text-sm font-medium transition-colors ${
                tab === t.key
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 정산 예정 및 완료 내역 탭 */}
      {tab === 'pendingAndHistory' && (
        <PendingAndHistoryTab
          pendingLines={pendingLines}
          pendingLoading={pendingLoading}
          brandView={brandView}
          nextSettlement={nextSettlement}
          historyItems={historyItems}
          historyLoading={historyLoading}
          mySettlementInfo={mySettlementInfo}
          settlementByBrand={settlementByBrand}
        />
      )}

      {/* 배송완료 내역 탭 */}
      {tab === 'delivered' && (
        <DeliveredEntriesSection
          description="아직 정산되지 않은 배송완료 내역입니다."
          lines={deliveredLines}
          loading={deliveredLoading}
          brandView={brandView}
          emptyMessage="배송완료된 건이 없습니다."
          amountLabel="총 정산금액 (VAT 포함)"
          showDateFilter
        />
      )}
    </div>
  );
};
